import type { ReferenceWordMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import runes from "@-/com.npmjs.runes2";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { assembleInfoMorphemeSliceDefinitionList } from "#assemble-info-morpheme-slice-definition-list";
import { hasLength } from "@-/com.npmjs.ts-array-length";

export function useGetReferenceWordMorphemeSliceQueryKey() {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  return (word: string) => ["infoWordMorphemeSlice", word, { staticDb }] as const;
}

export const [useInfoWordMorphemeSliceQueryOptions, infoWordMorphemeSliceQueryFn] =
  createUseQueryOptions(
    useGetReferenceWordMorphemeSliceQueryKey,
    async ({ queryKey: [_, word, { staticDb }] }) => {
      // The word together with its definitions, PRC level data, and frequency
      // ranks.
      const matchedWord = await staticDb(import.meta.location).query.word.findFirst({
        columns: { id: true, word: true },
        where: { word },
        with: {
          biau: { columns: { rank: true } },
          definitions: { columns: { definition: true, pinyin: true } },
          prcData: {
            columns: { hsk2010Level: true, hsk2021Level: true, hsk2026Level: true },
          },
          subtlex: { columns: { rank: true } },
        },
      });
      if (matchedWord === undefined) {
        return null;
      }
      const characterGlyphs = runes(matchedWord.word);

      // The word's characters live in the grapheme table, joined by glyph rather
      // than a relation, so they're fetched separately.
      const characterGraphemeRows = hasLength(characterGlyphs, 0)
        ? []
        : await staticDb(import.meta.location).query.grapheme.findMany({
            columns: { glyph: true, strokeCount: true },
            where: { glyph: { in: characterGlyphs } },
          });

      const definitionList = assembleInfoMorphemeSliceDefinitionList(matchedWord.definitions);
      const prcData = matchedWord.prcData;
      const strokeCountByGlyph = new Map(
        characterGraphemeRows.map((row) => [row.glyph, row.strokeCount]),
      );
      const characters: ReferenceWordMorphemeSlice["characters"] = characterGlyphs.map(
        (grapheme) => ({
          grapheme,
          strokeCount: strokeCountByGlyph.get(grapheme) ?? null,
        }),
      );

      return {
        biau2Rank: matchedWord.biau?.rank ?? null,
        characters,
        definitionList,
        hsk2010Level: prcData?.hsk2010Level ?? null,
        hsk2021Level: prcData?.hsk2021Level ?? null,
        hsk2026Level: prcData?.hsk2026Level ?? null,
        morphemeType: "word" as const,
        subtlexWfRank: matchedWord.subtlex?.rank ?? null,
        word: matchedWord.word,
        wordId: matchedWord.id,
      };
    },
  );

// Static-DB-only, immutable at runtime, key carries no change-version — a resolved slice never goes
// stale, and the global QueryClient default `staleTime: Infinity` caches it so a remount resolves
// from cache instead of re-querying. See the grapheme slice for the rationale.
